import { useRef, useState, ReactNode } from 'react';
import {
    Button,
    ClickAwayListener,
    Grow,
    Paper,
    Popper,
    MenuItem,
    MenuList,
    ButtonProps,
    ButtonGroup,
} from '@mui/material';

import { noop } from '@labradorsports/utils';

interface Props {
    options: {
        text: string;
        action?: AnyFunction;
        icon?: ReactNode;
        disabled?: boolean;
    }[];
    forceDropdown?: boolean;
    testId?: string;
}

const StyledDropdownButton: FC<Props & ButtonProps> = ({
    children,
    options,
    className = '',
    forceDropdown,
    variant,
    color = 'primary',
    testId,
    ...buttonProps
}) => {
    const [open, setOpen] = useState(false);
    const anchorRef = useRef<HTMLButtonElement>(null);

    const handleToggle = () => {
        setOpen((prevOpen) => !prevOpen);
    };

    const handleClose = (event: Event) => {
        if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
            return;
        }

        setOpen(false);
    };

    const actionClick =
        (action: AnyFunction = noop) =>
        () => {
            setOpen(false);
            action();
        };

    const primaryButtonClick = () => {
        if (forceDropdown) {
            handleToggle();
        } else {
            actionClick(options[0].action)();
        }
    };

    return (
        <>
            <ButtonGroup className={className}>
                <Button
                    onClick={primaryButtonClick}
                    className={`styled-dropdown-button rounded-button ${className}`}
                    data-testid={testId ? `${testId}-primary` : undefined}
                    // style={{ padding: '6px 10px' }}
                    color={color}
                    variant={variant}
                    {...buttonProps}
                >
                    {options.length > 0 ? (
                        <>
                            {options[0].icon ? (
                                <span className="button-icon">
                                    {typeof options[0].icon === 'string' ? (
                                        <img src={options[0].icon} alt="" />
                                    ) : (
                                        options[0].icon
                                    )}
                                </span>
                            ) : null}
                            {options[0].text}
                        </>
                    ) : (
                        children
                    )}
                </Button>
                {options.length > 1 ? (
                    <Button
                        ref={anchorRef}
                        aria-controls={open ? 'split-button-menu' : undefined}
                        aria-expanded={open ? 'true' : undefined}
                        aria-haspopup="menu"
                        onClick={handleToggle}
                        className={`rounded-button ${className}`}
                        data-testid={testId ? `${testId}-menu` : undefined}
                        style={{ paddingLeft: 0, paddingRight: 0, minWidth: '30px' }}
                        color={color}
                        variant={variant}
                        {...buttonProps}
                    >
                        <svg viewBox="0 0 24 24" width="22" height="22" style={{ marginRight: '1px' }}>
                            <path
                                d="m7 10 5 5 5-5z"
                                style={{
                                    fill:
                                        variant === 'contained'
                                            ? 'var(--theme-secondary-foreground)'
                                            : 'var(--text-color-1)',
                                }}
                            ></path>
                        </svg>
                    </Button>
                ) : null}
            </ButtonGroup>

            <Popper
                open={open}
                className="styled-dropdown-button"
                anchorEl={anchorRef.current}
                role={undefined}
                transition
                disablePortal
                style={{ zIndex: 1000 }}
                nonce={undefined}
                onResize={undefined}
                onResizeCapture={undefined}
            >
                {({ TransitionProps, placement }) => (
                    <Grow
                        {...TransitionProps}
                        style={{
                            transformOrigin: placement === 'bottom' ? 'right top' : 'right bottom',
                        }}
                    >
                        <Paper>
                            <ClickAwayListener onClickAway={handleClose}>
                                <MenuList id="split-button-menu">
                                    {options.slice(1).map((option) => (
                                        <MenuItem
                                            key={option.text}
                                            onClick={actionClick(option.action)}
                                            disabled={option.disabled}
                                        >
                                            {option.icon ? (
                                                <span className="dropdown-button-icon">
                                                    {typeof option.icon === 'string' ? (
                                                        <img src={option.icon} alt="" />
                                                    ) : (
                                                        option.icon
                                                    )}
                                                </span>
                                            ) : null}
                                            {option.text}
                                        </MenuItem>
                                    ))}
                                </MenuList>
                            </ClickAwayListener>
                        </Paper>
                    </Grow>
                )}
            </Popper>
        </>
    );
};

export default StyledDropdownButton;
